import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Mail, Send, Users, User, CheckCircle, XCircle, AlertCircle,
    Wifi, WifiOff, Loader, ChevronDown, Eye, EyeOff, RefreshCw,
    Search, X, UserPlus
} from 'lucide-react';
import api from '../services/api';

const FILTERS = [
    { value: 'all', label: 'All Users' },
    { value: 'crew', label: 'Crew Only' },
    { value: 'artists', label: 'Artists Only' },
    { value: 'email_verified', label: 'Email Verified' },
    { value: 'subscribers', label: 'Active Subscribers' }
];

const TABS = [
    { key: 'single', label: 'Single User', icon: User },
    { key: 'multi',  label: 'Multiple Users', icon: UserPlus },
    { key: 'bulk',   label: 'Bulk Send', icon: Users }
];

const AdminEmail = () => {
    const [tab, setTab] = useState('single'); // 'single' | 'multi' | 'bulk'

    // SMTP status
    const [smtpStatus, setSmtpStatus] = useState(null);
    const [smtpMessage, setSmtpMessage] = useState('');
    const [testingSmtp, setTestingSmtp] = useState(false);

    // Shared user list (loaded once, reused by single + multi tabs)
    const [allUsers, setAllUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // ── Single tab state ─────────────────────────────────────────
    const [singleUser, setSingleUser] = useState(null);
    const [userSearch, setUserSearch] = useState('');
    const [userResults, setUserResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchRef = useRef(null);
    const [singleSubject, setSingleSubject] = useState('');
    const [singleBody, setSingleBody] = useState('');
    const [sendingSingle, setSendingSingle] = useState(false);
    const [singleResult, setSingleResult] = useState(null);

    // ── Multi tab state ──────────────────────────────────────────
    const [multiUsers, setMultiUsers] = useState([]);   // array of selected user objects
    const [multiSearch, setMultiSearch] = useState('');
    const [multiResults, setMultiResults] = useState([]);
    const [showMultiDropdown, setShowMultiDropdown] = useState(false);
    const multiSearchRef = useRef(null);
    const [multiSubject, setMultiSubject] = useState('');
    const [multiBody, setMultiBody] = useState('');
    const [sendingMulti, setSendingMulti] = useState(false);
    const [multiResult, setMultiResult] = useState(null);
    const [confirmMulti, setConfirmMulti] = useState(false);

    // ── Bulk tab state ───────────────────────────────────────────
    const [filter, setFilter] = useState('all');
    const [recipients, setRecipients] = useState([]);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [bulkSubject, setBulkSubject] = useState('');
    const [bulkBody, setBulkBody] = useState('');
    const [sendingBulk, setSendingBulk] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [confirmBulk, setConfirmBulk] = useState(false);

    // Preview toggle
    const [showPreview, setShowPreview] = useState(false);

    // ── SMTP test ─────────────────────────────────────────────────
    const testSmtp = async () => {
        setTestingSmtp(true);
        setSmtpStatus(null);
        try {
            const res = await api.get('/admin/email/test');
            setSmtpStatus(res.data.success ? 'ok' : 'error');
            setSmtpMessage(res.data.message);
        } catch (err) {
            setSmtpStatus('error');
            setSmtpMessage(err.response?.data?.message || err.message);
        } finally {
            setTestingSmtp(false);
        }
    };

    useEffect(() => { testSmtp(); }, []);

    // ── Load recipients when filter changes (bulk tab) ────────────
    const loadRecipients = useCallback(async () => {
        setLoadingRecipients(true);
        setRecipients([]);
        try {
            const res = await api.get(`/admin/email/recipients?filter=${filter}`);
            if (res.data.success) setRecipients(res.data.data);
        } catch {
            setRecipients([]);
        } finally {
            setLoadingRecipients(false);
        }
    }, [filter]);

    useEffect(() => {
        if (tab === 'bulk') loadRecipients();
    }, [tab, filter, loadRecipients]);

    // ── Load all users for single + multi search ──────────────────
    useEffect(() => {
        if ((tab !== 'single' && tab !== 'multi') || allUsers.length > 0) return;
        const load = async () => {
            setLoadingUsers(true);
            try {
                const res = await api.get('/admin/email/recipients?filter=all');
                if (res.data.success) setAllUsers(res.data.data);
            } catch { /* ignore */ } finally {
                setLoadingUsers(false);
            }
        };
        load();
    }, [tab, allUsers.length]);

    // ── Single: filter users as admin types ───────────────────────
    useEffect(() => {
        if (tab !== 'single') return;
        const q = userSearch.toLowerCase().trim();
        if (!q) { setUserResults(allUsers.slice(0, 8)); return; }
        const matches = allUsers.filter((u) =>
            (u.display_name || '').toLowerCase().includes(q) ||
            (u.username || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q)
        ).slice(0, 8);
        setUserResults(matches);
        setShowDropdown(true);
    }, [userSearch, allUsers, tab]);

    // ── Multi: filter users as admin types, exclude already selected ─
    useEffect(() => {
        if (tab !== 'multi') return;
        const q = multiSearch.toLowerCase().trim();
        const selectedIds = new Set(multiUsers.map((u) => u.id));
        const pool = allUsers.filter((u) => !selectedIds.has(u.id));
        if (!q) { setMultiResults(pool.slice(0, 8)); return; }
        const matches = pool.filter((u) =>
            (u.display_name || '').toLowerCase().includes(q) ||
            (u.username || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q)
        ).slice(0, 8);
        setMultiResults(matches);
        setShowMultiDropdown(true);
    }, [multiSearch, allUsers, multiUsers, tab]);

    // ── Close dropdowns on outside click ─────────────────────────
    useEffect(() => {
        const handler = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false);
            if (multiSearchRef.current && !multiSearchRef.current.contains(e.target)) setShowMultiDropdown(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ── Single helpers ────────────────────────────────────────────
    const selectUser = (u) => {
        setSingleUser(u); setUserSearch(''); setUserResults([]); setShowDropdown(false); setSingleResult(null);
    };
    const clearUser = () => { setSingleUser(null); setUserSearch(''); setUserResults([]); };

    // ── Multi helpers ─────────────────────────────────────────────
    const addMultiUser = (u) => {
        setMultiUsers((prev) => [...prev, u]);
        setMultiSearch(''); setMultiResults([]); setShowMultiDropdown(false); setMultiResult(null);
    };
    const removeMultiUser = (id) => setMultiUsers((prev) => prev.filter((u) => u.id !== id));

    // ── Sends ─────────────────────────────────────────────────────
    const handleSendSingle = async (e) => {
        e.preventDefault();
        if (!singleUser || !singleSubject.trim() || !singleBody.trim()) return;
        setSendingSingle(true); setSingleResult(null);
        try {
            const res = await api.post('/admin/email/send-single', {
                email: singleUser.email, subject: singleSubject, body: singleBody
            });
            setSingleResult({ success: res.data.success, message: res.data.message });
        } catch (err) {
            setSingleResult({ success: false, message: err.response?.data?.message || err.message });
        } finally { setSendingSingle(false); }
    };

    const handleSendMulti = async () => {
        if (multiUsers.length === 0 || !multiSubject.trim() || !multiBody.trim()) return;
        setSendingMulti(true); setMultiResult(null); setConfirmMulti(false);
        try {
            const res = await api.post('/admin/email/send-bulk', {
                userIds: multiUsers.map((u) => u.id),
                subject: multiSubject,
                body: multiBody
            });
            setMultiResult({ success: res.data.success, message: res.data.message, data: res.data.data });
        } catch (err) {
            setMultiResult({ success: false, message: err.response?.data?.message || err.message });
        } finally { setSendingMulti(false); }
    };

    const handleSendBulk = async () => {
        if (!bulkSubject.trim() || !bulkBody.trim() || recipients.length === 0) return;
        setSendingBulk(true); setBulkResult(null); setConfirmBulk(false);
        try {
            const res = await api.post('/admin/email/send-bulk', {
                filter, subject: bulkSubject, body: bulkBody
            });
            setBulkResult({ success: res.data.success, message: res.data.message, data: res.data.data });
        } catch (err) {
            setBulkResult({ success: false, message: err.response?.data?.message || err.message });
        } finally { setSendingBulk(false); }
    };

    // ── Preview ───────────────────────────────────────────────────
    const bodyValue = tab === 'single' ? singleBody : tab === 'multi' ? multiBody : bulkBody;
    const subjectValue = tab === 'single' ? singleSubject : tab === 'multi' ? multiSubject : bulkSubject;

    const previewHtml = `
<div style="background:#111827;padding:40px 20px;font-family:Arial,Helvetica,sans-serif;">
  <div style="max-width:600px;margin:0 auto;">
    <div style="text-align:center;margin-bottom:32px;">
      <h1 style="margin:0;font-size:28px;font-weight:900;color:#60a5fa;">The Deep Wave</h1>
      <p style="margin:4px 0 0;color:#6b7280;font-size:14px;">Arts &amp; Sound Ensemble</p>
    </div>
    <div style="background:#1f2937;border:1px solid rgba(255,255,255,0.1);border-radius:12px;padding:32px;color:#f9fafb;">
      ${bodyValue || '<p style="color:#6b7280;margin:0;">Your message will appear here…</p>'}
    </div>
    <div style="text-align:center;margin-top:24px;color:#4b5563;font-size:12px;line-height:1.8;">
      <p style="margin:0;">© 2018 The Deep Wave. All rights reserved.</p>
      <p style="margin:2px 0 0;">
        <a href="#" style="color:#6b7280;text-decoration:none;">thedeepwave.com</a>
        &nbsp;·&nbsp;
        <a href="#" style="color:#6b7280;text-decoration:none;">Privacy</a>
        &nbsp;·&nbsp;
        <a href="#" style="color:#6b7280;text-decoration:none;">Terms</a>
      </p>
    </div>
  </div>
</div>`;

    const handleTabChange = (key) => {
        setTab(key);
        setSingleResult(null); setMultiResult(null); setBulkResult(null);
        setConfirmBulk(false); setConfirmMulti(false);
    };

    // ── Shared field classes ──────────────────────────────────────
    const inputCls = 'w-full bg-gray-800 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-blue-500';
    const labelCls = 'block text-xs text-gray-400 mb-1.5 font-medium uppercase tracking-wide';

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-4xl mx-auto px-6">

                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-black gradient-text mb-1">Email Center</h1>
                    <p className="text-gray-400 text-sm">Send messages to users directly from The Deep Wave</p>
                </div>

                {/* SMTP status bar */}
                <div className={`glass-card p-4 mb-6 flex items-center justify-between gap-4 border ${
                    smtpStatus === 'ok' ? 'border-green-500/30' :
                    smtpStatus === 'error' ? 'border-red-500/30' : 'border-white/10'
                }`}>
                    <div className="flex items-center gap-3">
                        {testingSmtp ? <Loader className="w-5 h-5 text-blue-400 animate-spin" />
                            : smtpStatus === 'ok' ? <Wifi className="w-5 h-5 text-green-400" />
                            : smtpStatus === 'error' ? <WifiOff className="w-5 h-5 text-red-400" />
                            : <Wifi className="w-5 h-5 text-gray-500" />}
                        <div>
                            <p className={`text-sm font-medium ${
                                smtpStatus === 'ok' ? 'text-green-400' :
                                smtpStatus === 'error' ? 'text-red-400' : 'text-gray-400'
                            }`}>
                                {testingSmtp ? 'Testing SMTP connection…'
                                    : smtpStatus === 'ok' ? 'SMTP Connected'
                                    : smtpStatus === 'error' ? 'SMTP Connection Failed'
                                    : 'SMTP status unknown'}
                            </p>
                            {smtpMessage && <p className="text-xs text-gray-500 mt-0.5">{smtpMessage}</p>}
                        </div>
                    </div>
                    <button onClick={testSmtp} disabled={testingSmtp}
                        className="text-xs text-gray-400 hover:text-white flex items-center gap-1 transition-colors">
                        <RefreshCw className={`w-3.5 h-3.5 ${testingSmtp ? 'animate-spin' : ''}`} />
                        Retest
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap gap-2 mb-6">
                    {TABS.map(({ key, label, icon: Icon }) => (
                        <button key={key} onClick={() => handleTabChange(key)}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                                tab === key ? 'bg-blue-600 text-white' : 'glass text-gray-400 hover:text-white'
                            }`}>
                            <Icon className="w-4 h-4" />
                            {label}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* ── Compose panel ─────────────────────── */}
                    <div className="glass-card p-6">
                        <h2 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
                            <Mail className="w-5 h-5 text-blue-400" />
                            Compose
                        </h2>

                        {/* ── Single tab ─── */}
                        {tab === 'single' && (
                            <form onSubmit={handleSendSingle} className="space-y-4">
                                <div ref={searchRef}>
                                    <label className={labelCls}>To</label>
                                    {singleUser ? (
                                        <div className="flex items-center gap-2 px-3 py-2 bg-blue-600/20 border border-blue-500/30 rounded-lg">
                                            <div className="w-7 h-7 rounded-full bg-blue-600/40 flex items-center justify-center flex-shrink-0">
                                                <User className="w-3.5 h-3.5 text-blue-300" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-white font-medium truncate">{singleUser.display_name || singleUser.username}</p>
                                                <p className="text-xs text-blue-300 truncate">{singleUser.email}</p>
                                            </div>
                                            <button type="button" onClick={clearUser} aria-label="Clear selected user" className="text-gray-400 hover:text-white flex-shrink-0">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                            <input type="text" value={userSearch}
                                                onChange={(e) => setUserSearch(e.target.value)}
                                                onFocus={() => setShowDropdown(allUsers.length > 0)}
                                                placeholder={loadingUsers ? 'Loading users…' : 'Search by name, username, or email'}
                                                className={`${inputCls} pl-9`} autoComplete="off" />
                                            {showDropdown && userResults.length > 0 && (
                                                <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-gray-800 border border-white/10 rounded-lg shadow-xl overflow-hidden">
                                                    {userResults.map((u) => (
                                                        <button key={u.id} type="button" onClick={() => selectUser(u)}
                                                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-700 transition-colors text-left">
                                                            <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                                                                <User className="w-3.5 h-3.5 text-gray-400" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm text-white font-medium truncate">
                                                                    {u.display_name || u.username}
                                                                    {u.username && u.display_name && <span className="text-gray-500 font-normal ml-1">@{u.username}</span>}
                                                                </p>
                                                                <p className="text-xs text-gray-400 truncate">{u.email}</p>
                                                            </div>
                                                            {!!u.is_artist && <span className="text-xs text-purple-400 flex-shrink-0">Artist</span>}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className={labelCls}>Subject</label>
                                    <input type="text" value={singleSubject} onChange={(e) => setSingleSubject(e.target.value)}
                                        placeholder="Your subject line" className={inputCls} required />
                                </div>

                                <div>
                                    <label className={labelCls}>Body (HTML supported)</label>
                                    <textarea value={singleBody} onChange={(e) => setSingleBody(e.target.value)}
                                        rows={10} placeholder="<p>Your message here…</p>"
                                        className={`${inputCls} font-mono resize-y`} required />
                                </div>

                                {singleResult && (
                                    <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                                        singleResult.success
                                            ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                                            : 'bg-red-500/10 text-red-400 border border-red-500/20'
                                    }`}>
                                        {singleResult.success ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                                        {singleResult.message}
                                    </div>
                                )}

                                <button type="submit" disabled={sendingSingle || smtpStatus !== 'ok' || !singleUser}
                                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors">
                                    {sendingSingle ? <><Loader className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send Email</>}
                                </button>
                            </form>
                        )}

                        {/* ── Multiple Users tab ─── */}
                        {tab === 'multi' && (
                            <div className="space-y-4">
                                {/* Search + chips */}
                                <div ref={multiSearchRef}>
                                    <label className={labelCls}>To</label>

                                    {/* Selected chips */}
                                    {multiUsers.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-2">
                                            {multiUsers.map((u) => (
                                                <div key={u.id} className="flex items-center gap-1.5 pl-2 pr-1 py-1 bg-blue-600/20 border border-blue-500/30 rounded-full text-xs">
                                                    <span className="text-white font-medium">{u.display_name || u.username}</span>
                                                    <button type="button" onClick={() => removeMultiUser(u.id)}
                                                        aria-label={`Remove ${u.display_name || u.username}`}
                                                        className="w-4 h-4 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors">
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Search input */}
                                    <div className="relative">
                                        <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                        <input type="text" value={multiSearch}
                                            onChange={(e) => setMultiSearch(e.target.value)}
                                            onFocus={() => setShowMultiDropdown(allUsers.length > 0)}
                                            placeholder={loadingUsers ? 'Loading users…' : 'Search by name, username, or email'}
                                            className={`${inputCls} pl-9`} autoComplete="off" />
                                        {showMultiDropdown && multiResults.length > 0 && (
                                            <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-gray-800 border border-white/10 rounded-lg shadow-xl overflow-hidden">
                                                {multiResults.map((u) => (
                                                    <button key={u.id} type="button" onClick={() => addMultiUser(u)}
                                                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-700 transition-colors text-left">
                                                        <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                                                            <User className="w-3.5 h-3.5 text-gray-400" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm text-white font-medium truncate">
                                                                {u.display_name || u.username}
                                                                {u.username && u.display_name && <span className="text-gray-500 font-normal ml-1">@{u.username}</span>}
                                                            </p>
                                                            <p className="text-xs text-gray-400 truncate">{u.email}</p>
                                                        </div>
                                                        {!!u.is_artist && <span className="text-xs text-purple-400 flex-shrink-0">Artist</span>}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {multiUsers.length > 0 && (
                                        <p className="text-xs text-gray-500 mt-1.5">
                                            {multiUsers.length} recipient{multiUsers.length !== 1 ? 's' : ''} selected
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className={labelCls}>Subject</label>
                                    <input type="text" value={multiSubject} onChange={(e) => setMultiSubject(e.target.value)}
                                        placeholder="Your subject line" className={inputCls} />
                                </div>

                                <div>
                                    <label className={labelCls}>Body (HTML supported)</label>
                                    <textarea value={multiBody} onChange={(e) => setMultiBody(e.target.value)}
                                        rows={10} placeholder="<p>Your message here…</p>"
                                        className={`${inputCls} font-mono resize-y`} />
                                </div>

                                {multiResult && (
                                    <div className={`p-3 rounded-lg text-sm border ${
                                        multiResult.success
                                            ? 'bg-green-500/10 text-green-400 border-green-500/20'
                                            : 'bg-red-500/10 text-red-400 border-red-500/20'
                                    }`}>
                                        <div className="flex items-start gap-2">
                                            {multiResult.success ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                                            <span>{multiResult.message}</span>
                                        </div>
                                        {multiResult.data?.errors?.length > 0 && (
                                            <details className="mt-2">
                                                <summary className="text-xs cursor-pointer text-gray-500">
                                                    {multiResult.data.errors.length} failed
                                                </summary>
                                                <ul className="mt-1 text-xs text-gray-500 space-y-0.5">
                                                    {multiResult.data.errors.map((e, i) => (
                                                        <li key={i}>{e.to}: {e.error}</li>
                                                    ))}
                                                </ul>
                                            </details>
                                        )}
                                    </div>
                                )}

                                {!confirmMulti ? (
                                    <button onClick={() => setConfirmMulti(true)}
                                        disabled={sendingMulti || smtpStatus !== 'ok' || multiUsers.length === 0 || !multiSubject.trim() || !multiBody.trim()}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors">
                                        <Send className="w-4 h-4" />
                                        Send to {multiUsers.length} Recipient{multiUsers.length !== 1 ? 's' : ''}
                                    </button>
                                ) : (
                                    <div className="space-y-2">
                                        <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-sm">
                                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                            <span>This will send <strong>{multiUsers.length}</strong> email{multiUsers.length !== 1 ? 's' : ''}. This cannot be undone.</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => setConfirmMulti(false)}
                                                className="flex-1 py-2.5 glass text-gray-300 hover:text-white rounded-lg text-sm font-medium transition-colors">
                                                Cancel
                                            </button>
                                            <button onClick={handleSendMulti} disabled={sendingMulti}
                                                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors">
                                                {sendingMulti ? <><Loader className="w-4 h-4 animate-spin" /> Sending…</> : 'Confirm Send'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Bulk tab ─── */}
                        {tab === 'bulk' && (
                            <div className="space-y-4">
                                <div>
                                    <label className={labelCls}>Recipients</label>
                                    <div className="relative">
                                        <select value={filter} onChange={(e) => { setFilter(e.target.value); setBulkResult(null); setConfirmBulk(false); }}
                                            className={`${inputCls} appearance-none pr-8`}>
                                            {FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                                        </select>
                                        <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1.5">
                                        {loadingRecipients ? 'Loading…' : `${recipients.length} recipient${recipients.length !== 1 ? 's' : ''} matched`}
                                    </p>
                                </div>

                                <div>
                                    <label className={labelCls}>Subject</label>
                                    <input type="text" value={bulkSubject} onChange={(e) => setBulkSubject(e.target.value)}
                                        placeholder="Your subject line" className={inputCls} />
                                </div>

                                <div>
                                    <label className={labelCls}>Body (HTML supported)</label>
                                    <textarea value={bulkBody} onChange={(e) => setBulkBody(e.target.value)}
                                        rows={10} placeholder="<p>Your message here…</p>"
                                        className={`${inputCls} font-mono resize-y`} />
                                </div>

                                {bulkResult && (
                                    <div className={`p-3 rounded-lg text-sm border ${
                                        bulkResult.success
                                            ? 'bg-green-500/10 text-green-400 border-green-500/20'
                                            : 'bg-red-500/10 text-red-400 border-red-500/20'
                                    }`}>
                                        <div className="flex items-start gap-2">
                                            {bulkResult.success ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                                            <span>{bulkResult.message}</span>
                                        </div>
                                        {bulkResult.data?.errors?.length > 0 && (
                                            <details className="mt-2">
                                                <summary className="text-xs cursor-pointer text-gray-500">
                                                    {bulkResult.data.errors.length} failed address{bulkResult.data.errors.length !== 1 ? 'es' : ''}
                                                </summary>
                                                <ul className="mt-1 text-xs text-gray-500 space-y-0.5">
                                                    {bulkResult.data.errors.map((e, i) => <li key={i}>{e.to}: {e.error}</li>)}
                                                </ul>
                                            </details>
                                        )}
                                    </div>
                                )}

                                {!confirmBulk ? (
                                    <button onClick={() => setConfirmBulk(true)}
                                        disabled={sendingBulk || smtpStatus !== 'ok' || recipients.length === 0 || !bulkSubject.trim() || !bulkBody.trim()}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors">
                                        <Send className="w-4 h-4" />
                                        Send to {recipients.length} Recipient{recipients.length !== 1 ? 's' : ''}
                                    </button>
                                ) : (
                                    <div className="space-y-2">
                                        <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-sm">
                                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                            <span>This will send <strong>{recipients.length}</strong> emails. This cannot be undone.</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => setConfirmBulk(false)}
                                                className="flex-1 py-2.5 glass text-gray-300 hover:text-white rounded-lg text-sm font-medium transition-colors">
                                                Cancel
                                            </button>
                                            <button onClick={handleSendBulk} disabled={sendingBulk}
                                                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors">
                                                {sendingBulk ? <><Loader className="w-4 h-4 animate-spin" /> Sending…</> : 'Confirm Send'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ── Preview panel ──────────────────────── */}
                    <div className="glass-card p-6 flex flex-col">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="text-lg font-bold text-white flex items-center gap-2">
                                <Eye className="w-5 h-5 text-purple-400" />
                                Preview
                            </h2>
                            <button onClick={() => setShowPreview(!showPreview)}
                                className="text-xs text-gray-400 hover:text-white flex items-center gap-1 transition-colors">
                                {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                {showPreview ? 'Hide' : 'Show'} preview
                            </button>
                        </div>

                        {/* Subject preview */}
                        <div className="mb-4 p-3 bg-gray-800 rounded-lg border border-white/10">
                            <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Subject</p>
                            <p className="text-white text-sm font-medium">
                                {subjectValue || <span className="text-gray-600 italic">No subject yet…</span>}
                            </p>
                        </div>

                        {/* Email preview */}
                        <div className="flex-1 rounded-lg overflow-hidden border border-white/10 min-h-64">
                            {showPreview ? (
                                <div dangerouslySetInnerHTML={{ __html: previewHtml }}
                                    className="w-full overflow-y-auto" style={{ maxHeight: '600px' }} />
                            ) : (
                                <div className="flex items-center justify-center h-64 text-gray-600 text-sm">
                                    <div className="text-center">
                                        <Eye className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                        <p>Click "Show preview" to render the email</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Recipient preview — multi */}
                        {tab === 'multi' && multiUsers.length > 0 && (
                            <div className="mt-4">
                                <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide font-medium">
                                    Selected Recipients ({multiUsers.length})
                                </p>
                                <div className="max-h-40 overflow-y-auto space-y-1">
                                    {multiUsers.map((r) => (
                                        <div key={r.id} className="flex items-center gap-2 text-xs text-gray-400 py-1 border-b border-white/5">
                                            <span className="text-gray-300 font-medium truncate">{r.display_name || r.username || '—'}</span>
                                            <span className="text-gray-600 truncate">{r.email}</span>
                                            {!!r.is_artist && <span className="text-purple-400 flex-shrink-0">Artist</span>}
                                            {!!r.email_verified && <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Recipient preview — bulk */}
                        {tab === 'bulk' && recipients.length > 0 && (
                            <div className="mt-4">
                                <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide font-medium">
                                    Recipient Preview ({recipients.length} total)
                                </p>
                                <div className="max-h-40 overflow-y-auto space-y-1">
                                    {recipients.slice(0, 50).map((r) => (
                                        <div key={r.id} className="flex items-center gap-2 text-xs text-gray-400 py-1 border-b border-white/5">
                                            <span className="text-gray-300 font-medium truncate">{r.display_name || r.username || '—'}</span>
                                            <span className="text-gray-600 truncate">{r.email}</span>
                                            {!!r.is_artist && <span className="text-purple-400 flex-shrink-0">Artist</span>}
                                            {!!r.email_verified && <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />}
                                        </div>
                                    ))}
                                    {recipients.length > 50 && (
                                        <p className="text-xs text-gray-600 text-center pt-1">…and {recipients.length - 50} more</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminEmail;
